import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Car, BadgeEuro, Wallet, MessageCircle, ShieldCheck, Sparkles, Wrench, ChevronRight } from "lucide-react";
import api from "../lib/api";
import VehicleCard from "../components/VehicleCard";
import { buildWhatsappUrl, openWhatsapp, WA_MESSAGES, formatEUR } from "../lib/contact";

const HERO_URL = "https://static.prod-images.emergentagent.com/jobs/faecaba3-024e-4a5d-b93a-c37660cf4c29/images/d87c10e6d180a30229736a386b8ad9efee13562f34ac3f13f83a89b5d130d620.png";
const DAMAGED_URL = "https://static.prod-images.emergentagent.com/jobs/faecaba3-024e-4a5d-b93a-c37660cf4c29/images/20ff8ae576b57b0b4a61d9876576587c215b72afbd6818f0a75b4624576967b1.png";

const TRUST_BADGES = [
  { label: "Autohändler in Wien", icon: ShieldCheck },
  { label: "Ankauf & Verkauf", icon: BadgeEuro },
  { label: "Auch beschädigte Fahrzeuge", icon: Wrench },
  { label: "Finanzierung möglich", icon: Wallet },
  { label: "Digitale Fahrzeugbewertung", icon: Sparkles },
];

const Home = () => {
  const [featured, setFeatured] = React.useState([]);

  React.useEffect(() => {
    api.get("/vehicles", { params: { sort: "newest" } })
      .then((r) => setFeatured(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-[var(--na-bg)] text-white" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[68vh] lg:min-h-[74vh] w-full overflow-hidden grain">
        <img src={HERO_URL} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A22]/95 via-[#1A1A22]/70 to-[#1A1A22]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A22] via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-14 lg:pt-16 lg:pb-20 min-h-[inherit] flex flex-col justify-center">
          <div className="max-w-2xl fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 border border-white/15 bg-white/5 rounded-xl text-[10px] uppercase tracking-[0.25em] text-zinc-300">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--na-red)]" />
              Premium Cars · Wien · seit 2024
            </div>

            <h1 className="font-display font-bold tracking-tighter leading-[0.95] text-white">
              <span className="flex items-center gap-2 sm:gap-4 lg:gap-5 flex-nowrap whitespace-nowrap">
                <img
                  src="/na-mark.svg"
                  alt="NA"
                  className="h-9 sm:h-14 lg:h-20 xl:h-24 w-auto select-none drop-shadow-[0_0_24px_rgba(229,32,32,0.18)] flex-shrink-0"
                  draggable={false}
                />
                <span className="silver-gradient-text text-4xl sm:text-5xl lg:text-7xl xl:text-8xl">Automobile</span>
              </span>
              <span className="block silver-gradient-text text-4xl sm:text-5xl lg:text-7xl xl:text-8xl mt-2 lg:mt-3">
                Drive your dream.
              </span>
            </h1>

            <p className="mt-6 text-base lg:text-lg text-zinc-300 leading-relaxed max-w-xl">
              Premium Fahrzeuge, transparente Beratung und digitale Fahrzeugbewertung in Österreich.
              Ankauf, Verkauf und Finanzierung – alles aus einer Hand.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/fahrzeuge"
                data-testid="hero-cta-inventory"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-2xl font-medium tracking-wide transition-colors red-glow"
              >
                Fahrzeuge ansehen <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/fahrzeug-verkaufen"
                data-testid="hero-cta-sell"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-[#1A1A22] rounded-2xl font-medium tracking-wide hover:bg-zinc-200 transition-colors"
              >
                Fahrzeug verkaufen
              </Link>
              <Link
                to="/finanzierung"
                data-testid="hero-cta-financing"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white rounded-2xl font-medium tracking-wide hover:bg-white/5 transition-colors"
              >
                Finanzierung berechnen
              </Link>
              <a
                href={buildWhatsappUrl(WA_MESSAGES.general)}
                onClick={openWhatsapp(WA_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#25D366] text-[#1A1A22] rounded-2xl font-medium tracking-wide hover:bg-[#20bd5a] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Anfrage
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust ticker — now a clean section below the hero, no longer overlapping CTAs */}
      <section className="border-y border-white/5 bg-[var(--na-bg-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 lg:py-4 flex flex-wrap gap-x-8 gap-y-2 items-center justify-center md:justify-start text-xs uppercase tracking-widest">
          {TRUST_BADGES.map((b) => (
            <span key={b.label} className="inline-flex items-center gap-2 text-zinc-300">
              <b.icon className="w-3.5 h-3.5 text-[var(--na-red)]" strokeWidth={1.8} />
              {b.label}
            </span>
          ))}
        </div>
      </section>

      {/* Featured vehicles — brighter silver-band section to break up the dark */}
      <section className="py-14 lg:py-20 bg-silver-band border-b border-silver-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 lg:mb-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[var(--na-red)] font-semibold mb-3">
                Featured Inventory
              </p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-white tracking-tighter">
                Aktuelle Top-Fahrzeuge
              </h2>
              <p className="text-zinc-400 mt-3 max-w-lg">
                Sorgfältig ausgewählte Premium-Fahrzeuge, sofort verfügbar in Hagenbrunn bei Wien.
              </p>
            </div>
            <Link
              to="/fahrzeuge"
              data-testid="home-view-all-link"
              className="inline-flex items-center gap-2 text-sm text-white hover:text-[var(--na-red)] transition-colors"
            >
              Alle Fahrzeuge ansehen <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {featured.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
            {featured.length === 0 && (
              [0, 1, 2].map((i) => <div key={i} className="bg-[#232330] border border-white/5 rounded-2xl h-96 animate-pulse" />)
            )}
          </div>
        </div>
      </section>

      {/* Services band — graphite radial to break up the silver section above */}
      <section className="py-12 lg:py-16 border-y border-silver-soft bg-graphite-radial relative grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <ServiceCard
            icon={Car}
            number="01"
            title="Fahrzeugverkauf"
            description="Handverlesene Premium-Fahrzeuge mit voller Transparenz, Servicehistorie und Garantie auf Wunsch."
            cta="Bestand ansehen"
            to="/fahrzeuge"
            testId="home-service-sales"
          />
          <ServiceCard
            icon={BadgeEuro}
            number="02"
            title="Fahrzeugankauf"
            description="Wir kaufen Ihr Fahrzeug zum fairen Marktpreis. Sofortbewertung dank KI, persönliche Abwicklung in Wien."
            cta="Fahrzeug bewerten"
            to="/fahrzeug-verkaufen"
            testId="home-service-purchase"
          />
          <ServiceCard
            icon={Wallet}
            number="03"
            title="Finanzierung"
            description="Maßgeschneiderte Finanzierungslösungen unserer Partnerbanken. Schnelle Zusage und transparente Konditionen."
            cta="Rate berechnen"
            to="/finanzierung"
            testId="home-service-financing"
          />
        </div>
      </section>

      {/* Damaged cars section — silver-band for alternation against the dark services strip above */}
      <section className="py-14 lg:py-20 relative bg-silver-band border-y border-silver-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative">
            <img
              src={DAMAGED_URL}
              alt="Wir kaufen auch beschädigte Fahrzeuge"
              className="rounded-2xl w-full aspect-[4/5] object-cover border border-white/5"
            />
            <div className="absolute -bottom-6 -right-4 lg:-bottom-8 lg:-right-8 bg-[var(--na-red)] text-white p-5 lg:p-6 rounded-2xl w-[240px] sm:w-[260px] lg:w-[280px] shadow-2xl shadow-[var(--na-red)]/20 hidden sm:block">
              <p className="text-[10px] uppercase tracking-widest opacity-80">Auch bei</p>
              <p className="font-display text-xl sm:text-2xl font-bold leading-tight mt-1 break-words">
                Motor- &amp;<br />Karosserieschaden
              </p>
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[var(--na-red)] font-semibold mb-4">
              Ankauf Spezial
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-white tracking-tighter leading-[1.05]">
              Wir kaufen auch <span className="text-[var(--na-red)]">beschädigte Fahrzeuge</span>
            </h2>
            <p className="text-zinc-300 mt-6 leading-relaxed">
              NA Automobile kauft nicht nur gepflegte Gebrauchtwagen, sondern auch Fahrzeuge mit
              technischen Problemen, Karosserieschäden, Motorschäden oder nicht fahrbereite Fahrzeuge.
              Beschreiben Sie Ihr Fahrzeug online und erhalten Sie eine erste unverbindliche Einschätzung.
            </p>

            <ul className="mt-8 space-y-3 text-sm text-zinc-300">
              {[
                "Motorschaden, Getriebeschaden, Elektronikprobleme",
                "Unfallschaden, Karosserieschaden",
                "Nicht fahrbereit / Standschaden",
                "Sofortige KI-Vorbewertung in unter 30 Sekunden",
                "Persönliche Abholung in ganz Österreich möglich",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <ChevronRight className="w-4 h-4 text-[var(--na-red)] mt-0.5 flex-shrink-0" />
                  {t}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/fahrzeug-verkaufen"
                data-testid="home-damaged-cta"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl font-medium tracking-wide transition-colors red-glow"
              >
                Fahrzeug bewerten lassen <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={buildWhatsappUrl(WA_MESSAGES.sellCar)}
                onClick={openWhatsapp(WA_MESSAGES.sellCar)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-damaged-whatsapp"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#25D366] text-[#1A1A22] rounded-xl font-medium tracking-wide hover:bg-[#20bd5a] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip — tighter spacing, sits just above the footer */}
      <section className="py-8 lg:py-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h3 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tighter">
            Bereit für Ihr nächstes Fahrzeug?
          </h3>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Lassen Sie sich persönlich beraten – telefonisch, per WhatsApp oder direkt an unserem
            Standort in 2201 Hagenbrunn, direkt vor Wien.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Link
              to="/kontakt"
              data-testid="home-cta-contact"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 hover:bg-white/5 text-white rounded-xl font-medium tracking-wide transition-colors"
            >
              Kontakt aufnehmen
            </Link>
            <a
              href={buildWhatsappUrl(WA_MESSAGES.general)}
              onClick={openWhatsapp(WA_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-cta-whatsapp-bottom"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-[#1A1A22] rounded-xl font-medium tracking-wide hover:bg-[#20bd5a] transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp starten
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServiceCard = ({ icon: Icon, number, title, description, cta, to, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group bg-[#232330] border border-white/5 hover:border-white/15 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1"
  >
    <div className="flex items-start justify-between mb-6">
      <div className="w-12 h-12 rounded-xl bg-[var(--na-red)]/10 text-[var(--na-red)] flex items-center justify-center group-hover:bg-[var(--na-red)] group-hover:text-white transition-colors">
        <Icon className="w-5 h-5" strokeWidth={1.6} />
      </div>
      <span className="font-display text-4xl font-light text-zinc-800 group-hover:text-zinc-600 transition-colors">{number}</span>
    </div>
    <h3 className="font-display text-2xl font-bold text-white tracking-tight">{title}</h3>
    <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{description}</p>
    <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white group-hover:text-[var(--na-red)] transition-colors">
      {cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
    </div>
  </Link>
);

export default Home;
